import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { CHAT_PARTS_MAX } from '@arthelper/shared/chat-parts';
import { Button } from '@artstorefronts/ui';
import { X, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import ChatDisplay from '../ChatDisplay';
import RecentPartNotice from '../Chat/RecentPartNotice';
import ChatEvaluation from '../ChatEvaluation';
import AdminChatDetails from '../AdminChatDetails';

const ADMIN_CHAT_QUERY = gql`
  query AdminChat($id: ID!, $last: Int = ${CHAT_PARTS_MAX}) {
    chat(id: $id) {
      id
      title
      type
      targetId
      targetType
      createdAt
      updatedAt
      good
      notes
      user {
        asfId
        firstName
        lastName
        email
        stripeCustomerId
        isOldAsfMember
        numImages
        numChats
      }
      partCount
      parts(last: $last) {
        id
        role
        partIndex
        type
        content
        toolCall
        attachment
        reasoning
        usage
        metadata
        good
        notes
        createdAt
      }
      summary {
        summaryContent
      }
    }
  }
`;

interface EvalModalProps {
  chatId: string | null;
  onClose: () => void;
}

function EvalModal({ chatId, onClose }: EvalModalProps) {
  const { t } = useTranslation('chat');
  const { t: tc } = useTranslation('common');
  const { data, loading, error, refetch } = useQuery(ADMIN_CHAT_QUERY, {
    variables: { id: chatId },
    fetchPolicy: 'network-only',
    skip: !chatId,
  });

  if (!chatId) return null;

  if (loading) {
    return (
      <motion.div
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.08, ease: 'easeOut' }}
      >
        <div className="bg-white p-6 rounded-lg">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </motion.div>
    );
  }

  if (error || !data?.chat) {
    return (
      <motion.div
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.08, ease: 'easeOut' }}
      >
        <div className="bg-white p-6 rounded-lg max-w-md">
          <p className="text-red-600 mb-4">{t('eval-modal.load-failed')}</p>
          <Button onClick={onClose}>{tc('close')}</Button>
        </div>
      </motion.div>
    );
  }

  const chat = data.chat;

  return (
    <motion.div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.1, ease: 'easeOut' }}
    >
      <motion.div
        className="absolute bottom-0 left-0 right-0 bg-white h-[96vh] rounded-t-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 11, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 11, opacity: 0 }}
        transition={{
          duration: 0.1,
          ease: [0.4, 0, 0.2, 1],
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold">{chat.title}</h2>
            <p className="text-sm text-gray-600">{chat.user?.email}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex h-[calc(96vh-64px)]">
          {/* Left Panel (60%) - Chat Messages */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="p-4">
              <RecentPartNotice chat={chat} />
              <ChatDisplay chatParts={chat.parts} />
            </div>
          </div>

          {/* Right Panel (40%) - Evaluation Form and Details */}
          <div className="w-2/5 overflow-y-auto p-6 space-y-6 border-l bg-gray-50">
            <div className="text-left">
              <span className="text-xl font-semibold text-black tracking-tight">
                {chat.type === 'standalone'
                  ? t('eval-modal.open-chat')
                  : chat.type.charAt(0).toUpperCase() + chat.type.slice(1)}
              </span>
            </div>

            <ChatEvaluation
              chatId={chat.id}
              initialGood={chat.good}
              initialNotes={chat.notes}
              onSave={() => {
                refetch();
                onClose();
              }}
            />

            {/* Chat Details */}
            <AdminChatDetails chat={chat} />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default EvalModal;
